import { useState, useEffect, useCallback, useRef } from 'react'
import config from '../config'

// WebSocket event types
export type WebSocketEventType =
  | 'connected'
  | 'TEST_RUN_STARTED'
  | 'TEST_PROGRESS'
  | 'TEST_RUN_COMPLETED'
  | 'TEST_SUITE_UPDATED'
  | 'SCHEDULE_TRIGGERED'

export interface WebSocketMessage {
  type: WebSocketEventType
  data?: any
  message?: string
  timestamp: string
}

export interface TestRunStartedData {
  testSuiteId: number
  testSuiteName: string
  totalTests: number
  startedAt: string
}

export interface TestProgressData {
  testSuiteId: number
  currentTest: number
  totalTests: number
  passed: number
  failed: number
}

export interface TestRunCompletedData {
  testSuiteId: number
  testSuiteName: string
  passed: number
  failed: number
  totalTests: number
  duration: number
  status: 'completed' | 'failed'
}

interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void
  onTestRunStarted?: (data: TestRunStartedData) => void
  onTestProgress?: (data: TestProgressData) => void
  onTestRunCompleted?: (data: TestRunCompletedData) => void
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Event) => void
  autoReconnect?: boolean
  reconnectInterval?: number
  maxReconnectAttempts?: number
}

interface UseWebSocketReturn {
  isConnected: boolean
  lastMessage: WebSocketMessage | null
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error'
  reconnectAttempts: number
  connect: () => void
  disconnect: () => void
  send: (message: object) => void
}

export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const {
    onMessage,
    onTestRunStarted,
    onTestProgress,
    onTestRunCompleted,
    onConnect,
    onDisconnect,
    onError,
    autoReconnect = true,
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected')
  const [reconnectAttempts, setReconnectAttempts] = useState(0)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const manualDisconnectRef = useRef(false)

  const getWsUrl = useCallback(() => {
    // WebSocket is disabled in production (Lambda doesn't support WebSockets)
    // The app uses polling for real-time updates instead
    if (config.isProduction || config.apiUrl === '' || config.apiUrl === undefined) {
      return null
    }

    const token = localStorage.getItem('token')
    if (!token) return null

    // Convert HTTP URL to WebSocket URL (development only)
    const apiUrl = config.apiUrl
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws'
    const host = apiUrl.replace(/^https?:\/\//, '')

    return `${wsProtocol}://${host}/ws?token=${token}`
  }, [])

  const connect = useCallback(() => {
    const wsUrl = getWsUrl()
    if (!wsUrl) {
      console.warn('WebSocket: No auth token, cannot connect')
      return
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    manualDisconnectRef.current = false
    setConnectionState('connecting')

    try {
      wsRef.current = new WebSocket(wsUrl)

      wsRef.current.onopen = () => {
        console.log('WebSocket: Connected')
        setIsConnected(true)
        setConnectionState('connected')
        setReconnectAttempts(0)
        onConnect?.()
      }

      wsRef.current.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          setLastMessage(message)
          onMessage?.(message)

          // Call specific handlers based on event type
          switch (message.type) {
            case 'TEST_RUN_STARTED':
              onTestRunStarted?.(message.data)
              break
            case 'TEST_PROGRESS':
              onTestProgress?.(message.data)
              break
            case 'TEST_RUN_COMPLETED':
              onTestRunCompleted?.(message.data)
              break
          }
        } catch (err) {
          console.error('WebSocket: Failed to parse message', err)
        }
      }

      wsRef.current.onclose = () => {
        console.log('WebSocket: Disconnected')
        setIsConnected(false)
        setConnectionState('disconnected')
        onDisconnect?.()

        // Auto-reconnect logic
        if (autoReconnect && !manualDisconnectRef.current && reconnectAttempts < maxReconnectAttempts) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`WebSocket: Reconnecting... (attempt ${reconnectAttempts + 1})`)
            setReconnectAttempts(prev => prev + 1)
            connect()
          }, reconnectInterval)
        }
      }

      wsRef.current.onerror = (error) => {
        console.error('WebSocket: Error', error)
        setConnectionState('error')
        onError?.(error)
      }
    } catch (err) {
      console.error('WebSocket: Connection failed', err)
      setConnectionState('error')
    }
  }, [getWsUrl, onConnect, onDisconnect, onError, onMessage, onTestRunStarted, onTestProgress, onTestRunCompleted, autoReconnect, reconnectInterval, maxReconnectAttempts, reconnectAttempts])

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
    setConnectionState('disconnected')
  }, [])

  const send = useCallback((message: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket: Cannot send, not connected')
    }
  }, [])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Reconnect when token changes (login/logout)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'token') {
        if (event.newValue) {
          connect()
        } else {
          disconnect()
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [connect, disconnect])

  return {
    isConnected,
    lastMessage,
    connectionState,
    reconnectAttempts,
    connect,
    disconnect,
    send,
  }
}

export default useWebSocket
